import { useEffect } from 'react';
import { useAuthStore, demoService } from '@vesper/shared';

/**
 * Heartbeat scheduler for active demo sessions.
 *
 * Without this, the backend has no liveness signal between /demo/start
 * and /demo/end. Two visible side effects of that:
 *
 *   1. demo_analytics_sessions.last_active_at never moves past the
 *      session-creation timestamp, so the "avg session duration" KPI
 *      in the dashboard reads ~0 seconds for every still-active row.
 *   2. The demo session itself auto-expires 60 min after /start
 *      because demo.service.heartbeatDemoSession is what extends
 *      expires_at by another 60 min. A user who's actively using the
 *      demo for an hour and a half would get booted at the 60-min
 *      mark with no warning.
 *
 * Fix: while a demo user is authenticated, POST /demo/heartbeat every
 * 30 seconds. The endpoint updates both demo_sessions.expires_at
 * (rolling 60-min idle window) and demo_analytics_sessions.last_active_at
 * (so the duration KPI grows). Real (non-demo) users no-op the effect.
 *
 * Mounted once high in the App tree — like DemoSurfaceTracker.
 */
const HEARTBEAT_INTERVAL_MS = 30_000;

export function DemoHeartbeat(): null {
  const user = useAuthStore((s) => s.user);
  const sessionId = user?.isDemo ? user.demoSessionId ?? null : null;

  useEffect(() => {
    if (!sessionId) return;
    // Fire one immediately so last_active_at updates on mount (don't
    // wait 30s before the first ping); then schedule the interval.
    void demoService.heartbeatDemoSession(sessionId).catch(() => { /* best-effort */ });
    const id = window.setInterval(() => {
      void demoService.heartbeatDemoSession(sessionId).catch(() => {
        // Silently swallow — if the network is down or the session
        // already expired, the user will get the expiry response on
        // their next real API call. Heartbeat failures must never
        // surface to the demo flow.
      });
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [sessionId]);

  return null;
}
